import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "../CSS/Login.css";

import LogoImg from "../multimedia/Logo_universidad_UTM.png";
import IMG1 from "../multimedia/IMG1.png";
import IMG2 from "../multimedia/IMG2.png";
import IMG3 from "../multimedia/IMG3.png";
import IMG4 from "../multimedia/IMG4.png";
import IMG5 from "../multimedia/IMG5.png";

const Login = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const { login, isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const pendingRedirectRef = useRef(null);

  const slides = useMemo(
    () => [
      { src: IMG2, alt: "Entrada Principal" },
      { src: IMG1, alt: "Edificio Bienestar Estudiantil" },
      { src: IMG3, alt: "Aula de clases" },
      { src: IMG4, alt: "Tutorías académicas" },
      { src: IMG5, alt: "Estatua" },
    ],
    []
  );

  const redirectTo = useMemo(() => {
    const from = location.state?.from;
    const pathname = from?.pathname || "/";
    const search = from?.search || "";

    if (
      pathname === "/login" ||
      pathname.startsWith("/login?") ||
      pathname.startsWith("/login/")
    ) {
      return "/";
    }

    return `${pathname}${search}`;
  }, [location.state]);

  useEffect(() => {
    const stateMessage = location.state?.message;
    let storedMessage = "";

    try {
      storedMessage = sessionStorage.getItem("auth:loginMessage") || "";
      if (storedMessage) sessionStorage.removeItem("auth:loginMessage");
    } catch { }

    setStatusMessage(stateMessage || storedMessage || "");
  }, [location.state]);

  useEffect(() => {
    if (slides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 12000);

    return () => clearInterval(interval);
  }, [slides]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const nextTarget = pendingRedirectRef.current || redirectTo || "/";
    pendingRedirectRef.current = null;

    navigate(nextTarget, { replace: true, state: {} });
  }, [isAuthenticated, user, redirectTo, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === "email" ? value.toLowerCase() : value;

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (errors.general) {
      setErrors((prev) => ({
        ...prev,
        general: "",
      }));
    }

    if (statusMessage) {
      setStatusMessage("");
    }
  };

  const handleCapsLock = (e) => {
    if (typeof e.getModifierState === "function") {
      setCapsLock(e.getModifierState("CapsLock"));
    }
  };

  const normalizeEmail = (value = "") =>
    String(value || "").trim().toLowerCase();

  const validateForm = () => {
    const newErrors = {};
    const cleanEmail = normalizeEmail(formData.email);

    if (!cleanEmail) {
      newErrors.email = "El email es requerido";
    } else if (!/\S+@\S+\.\S+/.test(cleanEmail)) {
      newErrors.email = "El email no es válido";
    }

    if (!formData.password.trim()) {
      newErrors.password = "La contraseña es requerida";
    } else if (formData.password.length < 6) {
      newErrors.password = "La contraseña debe tener al menos 6 caracteres";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || loading) return;

    setLoading(true);
    setErrors({});

    try {
      const cleanEmail = normalizeEmail(formData.email);

      pendingRedirectRef.current = redirectTo || "/";
      await login(cleanEmail, formData.password);
    } catch (error) {
      pendingRedirectRef.current = null;
      console.error("Error en handleSubmit:", error);
      setErrors({
        general: error?.message || "Error al iniciar sesión",
      });
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated && user) {
    return null;
  }

  return (
    <div className="login-page">
      <div className="login-layout">
        <div className="login-visual-panel">
          <div className="login-visual-orb orb-one" />
          <div className="login-visual-orb orb-two" />

          <div className="login-visual-header">
            <img
              src={LogoImg}
              alt="Logo UTM"
              className="login-visual-header-logo"
              loading="eager"
              decoding="async"
              draggable="false"
            />

            <div className="login-visual-header-text">
              <h1>Sistema de</h1>
              <h2>AutoAprendizaje</h2>
            </div>
          </div>

          <div className="login-visual-center">
            <div className="login-visual-circle-wrap">
              <div className="login-visual-shape shape-one" />
              <div className="login-visual-shape shape-two" />
              <div className="login-visual-shape shape-three" />

              <div className="login-visual-circle">
                <div className="login-visual-slider">
                  {slides.map((slide, index) => (
                    <img
                      key={`${slide.alt}-${index}`}
                      src={slide.src}
                      alt={slide.alt}
                      className={`login-visual-slide ${index === currentSlide ? "active" : ""
                        }`}
                      draggable="false"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="login-visual-footer">
            <p>
              Copyright © 2026, Universidad Técnica de Manabí. Todos los
              derechos reservados.
            </p>
          </div>
        </div>

        <div className="login-form-panel">
          <div className="login-form-mobile-brand">
            <img
              src={LogoImg}
              alt="Logo UTM"
              className="login-form-mobile-logo"
              loading="eager"
              decoding="async"
              draggable="false"
            />
            <h1>Sistema de AutoAprendizaje</h1>
          </div>

          <div className="login-form-spacer" />

          <div className="login-form-center">
            <div className="login-form-card">
              <div className="login-form-title-wrap">
                <div className="login-form-title">
                  Iniciar Sesión
                  <span className="login-form-title-line" />
                </div>
              </div>

              <form onSubmit={handleSubmit} className="login-form">
                {errors.general && (
                  <div className="login-alert login-alert-error">
                    {errors.general}
                  </div>
                )}

                {statusMessage && !errors.general && (
                  <div className="login-alert login-alert-success">
                    {statusMessage}
                  </div>
                )}

                <div className="login-field">
                  <label htmlFor="email">Correo Electrónico</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Institucional o Personal"
                    autoComplete="email"
                    disabled={loading}
                    className={errors.email ? "input-error" : ""}
                  />
                  {errors.email && (
                    <span className="login-field-error">{errors.email}</span>
                  )}
                </div>

                <div className="login-field">
                  <div className="login-field-label-row">
                    <label htmlFor="password">Contraseña</label>

                    <div className={`caps-warning ${capsLock ? "visible" : ""}`}>
                      <svg
                        width="12"
                        height="12"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                      <span>Mayúsculas activadas</span>
                    </div>
                  </div>

                  <div className="login-password-wrap">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      onKeyDown={handleCapsLock}
                      onKeyUp={handleCapsLock}
                      onBlur={() => setCapsLock(false)}
                      placeholder="........."
                      autoComplete="current-password"
                      disabled={loading}
                      className={`${errors.password ? "input-error" : ""} ${!showPassword ? "password-hidden-style" : ""
                        }`}
                    />

                    <button
                      type="button"
                      className="toggle-password-btn"
                      onClick={() => setShowPassword((prev) => !prev)}
                      disabled={loading}
                      title={
                        showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                      }
                      aria-label={
                        showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                      }
                    >
                      {showPassword ? (
                        <svg
                          width="18"
                          height="18"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg
                          width="18"
                          height="18"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {errors.password && (
                    <span className="login-field-error">{errors.password}</span>
                  )}
                </div>

                <button
                  type="submit"
                  className="login-submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="login-submit-loading">
                      <span className="login-spinner" />
                      Iniciando sesión...
                    </span>
                  ) : (
                    "Entrar"
                  )}
                </button>

                <div className="login-help-link-wrap">
                  <a href="/ayuda" className="login-help-link">
                    Centro de ayuda
                  </a>
                </div>
              </form>
            </div>
          </div>

          <div className="login-form-footer">
            <div className="login-socials">
              <a
                href="https://www.utm.edu.ec/"
                target="_blank"
                rel="noreferrer"
                aria-label="Página Web"
                title="Página Web"
              >
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  <path d="M2 12h20" />
                </svg>
              </a>

              <a href="#" target="_blank" rel="noreferrer" aria-label="Instagram">
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>

              <a href="#" target="_blank" rel="noreferrer" aria-label="Facebook">
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
                </svg>
              </a>

              <a href="#" target="_blank" rel="noreferrer" aria-label="Twitter / X">
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                </svg>
              </a>
            </div>

            <div className="login-contact">
              <div className="login-contact-item">
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 8l7.89 5.26a2 2 0 0 0 2.22 0L21 8M5 19h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z"
                  />
                </svg>
                <span>soporte.autoaprendizaje@utm.edu.ec</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
